import type { ReportTableLayoutConfig } from "./use-report-table-layout.js";

export type OutgoingReportColumnId =
  | "date"
  | "recipient"
  | "platform"
  | "sender"
  | "origin"
  | "campaign"
  | "message"
  | "status";

export type IncomingReportColumnId =
  | "date"
  | "sender"
  | "platform"
  | "account"
  | "message"
  | "type";

export type MessageLogColumnId =
  | "date"
  | "direction"
  | "party"
  | "platform"
  | "account"
  | "origin"
  | "campaign"
  | "message"
  | "status";

export type CampaignReportColumnId =
  | "campaign"
  | "status"
  | "date"
  | "sender"
  | "template"
  | "recipients"
  | "processing"
  | "sent"
  | "delivered"
  | "read"
  | "failed"
  | "excluded"
  | "deliveryRate"
  | "readRate";

export const OUTGOING_REPORT_COLUMNS: ReportTableLayoutConfig<OutgoingReportColumnId> = {
  storageKey: "reports.outgoing.columns:v1",
  defaultOrder: [
    "date",
    "recipient",
    "platform",
    "sender",
    "origin",
    "campaign",
    "message",
    "status",
  ],
  requiredColumns: new Set(["recipient", "message"]),
  defaultWidths: {
    date: 132,
    recipient: 160,
    platform: 110,
    sender: 140,
    origin: 100,
    campaign: 140,
    message: 220,
    status: 110,
  },
  minWidths: {
    date: 100,
    recipient: 120,
    platform: 88,
    sender: 100,
    origin: 80,
    campaign: 100,
    message: 140,
    status: 88,
  },
  maxWidths: {
    date: 200,
    recipient: 280,
    platform: 160,
    sender: 240,
    origin: 160,
    campaign: 260,
    message: 420,
    status: 160,
  },
};

export const INCOMING_REPORT_COLUMNS: ReportTableLayoutConfig<IncomingReportColumnId> = {
  storageKey: "reports.incoming.columns:v1",
  defaultOrder: ["date", "sender", "platform", "account", "message", "type"],
  requiredColumns: new Set(["sender", "message"]),
  defaultWidths: {
    date: 132,
    sender: 160,
    platform: 110,
    account: 140,
    message: 240,
    type: 100,
  },
  minWidths: {
    date: 100,
    sender: 120,
    platform: 88,
    account: 100,
    message: 140,
    type: 80,
  },
  maxWidths: {
    date: 200,
    sender: 280,
    platform: 160,
    account: 240,
    message: 420,
    type: 140,
  },
};

export const MESSAGE_LOG_COLUMNS: ReportTableLayoutConfig<MessageLogColumnId> = {
  storageKey: "reports.message-log.columns:v1",
  defaultOrder: [
    "date",
    "direction",
    "party",
    "platform",
    "account",
    "origin",
    "campaign",
    "message",
    "status",
  ],
  requiredColumns: new Set(["direction", "party", "message"]),
  defaultWidths: {
    date: 132,
    direction: 88,
    party: 150,
    platform: 110,
    account: 130,
    origin: 100,
    campaign: 130,
    message: 200,
    status: 100,
  },
  minWidths: {
    date: 100,
    direction: 72,
    party: 110,
    platform: 88,
    account: 100,
    origin: 80,
    campaign: 100,
    message: 140,
    status: 88,
  },
  maxWidths: {
    date: 200,
    direction: 120,
    party: 260,
    platform: 160,
    account: 220,
    origin: 160,
    campaign: 240,
    message: 400,
    status: 150,
  },
  defaultHidden: ["account", "campaign"],
};

export const CAMPAIGN_REPORT_COLUMNS: ReportTableLayoutConfig<CampaignReportColumnId> = {
  storageKey: "reports.campaigns.columns:v1",
  defaultOrder: [
    "campaign",
    "status",
    "date",
    "sender",
    "template",
    "recipients",
    "processing",
    "sent",
    "delivered",
    "read",
    "failed",
    "excluded",
    "deliveryRate",
    "readRate",
  ],
  requiredColumns: new Set(["campaign"]),
  defaultWidths: {
    campaign: 180,
    status: 100,
    date: 120,
    sender: 130,
    template: 130,
    recipients: 88,
    processing: 88,
    sent: 72,
    delivered: 88,
    read: 72,
    failed: 72,
    excluded: 88,
    deliveryRate: 96,
    readRate: 88,
  },
  minWidths: {
    campaign: 140,
    status: 80,
    date: 96,
    sender: 100,
    template: 100,
    recipients: 64,
    processing: 64,
    sent: 56,
    delivered: 64,
    read: 56,
    failed: 56,
    excluded: 64,
    deliveryRate: 72,
    readRate: 72,
  },
  maxWidths: {
    campaign: 320,
    status: 140,
    date: 180,
    sender: 220,
    template: 220,
    recipients: 120,
    processing: 120,
    sent: 100,
    delivered: 120,
    read: 100,
    failed: 100,
    excluded: 120,
    deliveryRate: 140,
    readRate: 140,
  },
};

export type ContactImportReportColumnId =
  | "file"
  | "date"
  | "rows"
  | "created"
  | "restored"
  | "skipped"
  | "failed"
  | "warnings"
  | "report";

export const CONTACT_IMPORT_REPORT_COLUMNS: ReportTableLayoutConfig<ContactImportReportColumnId> = {
  storageKey: "reports.contact-imports.columns:v1",
  defaultOrder: ["file", "date", "rows", "created", "restored", "skipped", "failed", "warnings", "report"],
  requiredColumns: new Set(["file", "report"]),
  defaultWidths: {
    file: 200,
    date: 148,
    rows: 72,
    created: 80,
    restored: 84,
    skipped: 80,
    failed: 72,
    warnings: 88,
    report: 220,
  },
  minWidths: {
    file: 140,
    date: 120,
    rows: 56,
    created: 64,
    restored: 64,
    skipped: 64,
    failed: 56,
    warnings: 72,
    report: 72,
  },
  maxWidths: {
    file: 360,
    date: 200,
    rows: 100,
    created: 120,
    restored: 120,
    skipped: 120,
    failed: 110,
    warnings: 120,
    report: 280,
  },
};
